/**
 * Maintenance Logs hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useMaintenanceLogsRelayQuery as UseMaintenanceLogsRelayQuery,
  useMaintenanceLogsRelayQuery$variables,
  MaintenanceLogFilterInput,
  MaintenanceLogSortInput,
} from './__generated__/useMaintenanceLogsRelayQuery.graphql';
import type { useMaintenanceLogsRelayDirectQuery as UseMaintenanceLogsRelayDirectQuery } from './__generated__/useMaintenanceLogsRelayDirectQuery.graphql';
import type { useMaintenanceLogsRelayByIdQuery } from './__generated__/useMaintenanceLogsRelayByIdQuery.graphql';
import type { useMaintenanceLogsRelayPaginationFragment$key } from './__generated__/useMaintenanceLogsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for maintenance logs list
 */
const MaintenanceLogsListPaginationFragment = graphql`
  fragment useMaintenanceLogsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useMaintenanceLogsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "MaintenanceLogFilterInput" }
    sort: { type: "[MaintenanceLogSortInput!]" }
  ) {
    listMaintenanceLogs(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
    )
      @connection(
        key: "useMaintenanceLogsRelayPaginationFragment_listMaintenanceLogs"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          adminUserId
          createdOn
          hardwareOldId
          locationId
          maintenanceTaskOldId
          maintenanceTicketOldId
          notes
          organizationId
          systemId
          systemOldId
          timeTracking
          adminUser {
            id
            email
            firstName
            lastName
          }
          hardware {
            id
            oldId
            number
            hardwareType {
              id
              name
            }
            hardwareProduct {
              id
              primaryIdentifier
            }
          }
          location {
            id
            oldId
            name
          }
          maintenanceTask {
            id
            oldId
            name
            description
          }
          maintenanceTicket {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated maintenance logs list (uses fragment for Suspense hooks)
 */
const MaintenanceLogsListQuery = graphql`
  query useMaintenanceLogsRelayQuery(
    $first: Int
    $after: String
    $filter: MaintenanceLogFilterInput
    $sort: [MaintenanceLogSortInput!]
  ) {
    ...useMaintenanceLogsRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

/**
 * Direct query for maintenance logs list (for non-Suspense fetchQuery usage)
 */
const MaintenanceLogsListDirectQuery = graphql`
  query useMaintenanceLogsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: MaintenanceLogFilterInput
    $sort: [MaintenanceLogSortInput!]
  ) {
    listMaintenanceLogs(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          adminUserId
          createdOn
          hardwareOldId
          locationId
          maintenanceTaskOldId
          maintenanceTicketOldId
          notes
          organizationId
          systemId
          systemOldId
          timeTracking
          adminUser {
            id
            email
            firstName
            lastName
          }
          hardware {
            id
            oldId
            number
            hardwareType {
              id
              name
            }
            hardwareProduct {
              id
              primaryIdentifier
            }
          }
          location {
            id
            oldId
            name
          }
          maintenanceTask {
            id
            oldId
            name
            description
          }
          maintenanceTicket {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for single maintenance log by ID
 */
const MaintenanceLogByIdQuery = graphql`
  query useMaintenanceLogsRelayByIdQuery($id: ID!) {
    getMaintenanceLog(id: $id) {
      id
      oldId
      adminUserId
      createdOn
      hardwareOldId
      locationId
      maintenanceTaskOldId
      maintenanceTicketOldId
      notes
      organizationId
      systemId
      systemOldId
      timeTracking
      adminUser {
        id
        email
        firstName
        lastName
      }
      hardware {
        id
        oldId
        number
        hardwareType {
          id
          name
        }
        hardwareProduct {
          id
          primaryIdentifier
        }
      }
      location {
        id
        oldId
        name
      }
      maintenanceTask {
        id
        oldId
        name
        description
      }
      maintenanceTicket {
        id
        oldId
      }
      system {
        id
        oldId
        name
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { MaintenanceLogFilterInput, MaintenanceLogSortInput };

export interface LogAdminUserGraphQL {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface LogHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  hardwareType?: {
    id: string;
    name: string;
  } | null;
  hardwareProduct?: {
    id: string;
    primaryIdentifier?: string | null;
  } | null;
}

export interface LogLocationGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface LogMaintenanceTaskGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  description: string;
}

export interface LogMaintenanceTicketGraphQL {
  id: string;
  oldId?: string | null;
}

export interface LogSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface MaintenanceLogGraphQL {
  id: string;
  oldId?: string | null;
  adminUserId: string;
  createdOn: string;
  hardwareOldId?: string | null;
  locationId?: string | null;
  maintenanceTaskOldId: string;
  maintenanceTicketOldId?: string | null;
  notes?: string | null;
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId: string;
  timeTracking: number;
  adminUser?: LogAdminUserGraphQL | null;
  hardware?: LogHardwareGraphQL | null;
  location?: LogLocationGraphQL | null;
  maintenanceTask?: LogMaintenanceTaskGraphQL | null;
  maintenanceTicket?: LogMaintenanceTicketGraphQL | null;
  system?: LogSystemGraphQL | null;
}

export interface MaintenanceLogQueryFilter {
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  locationId?: string;
  search?: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching maintenance logs list using Relay (Suspense)
 */
export function useMaintenanceLogsRelay(
  filter?: MaintenanceLogQueryFilter,
  options?: {
    limit?: number;
    sort?: MaintenanceLogSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const variables: useMaintenanceLogsRelayQuery$variables = {
    first: limit,
    filter: buildLogRelayFilter(filter),
    sort,
  };

  const queryData = useLazyLoadQuery<UseMaintenanceLogsRelayQuery>(
    MaintenanceLogsListQuery,
    variables
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } =
    usePaginationFragment<
      UseMaintenanceLogsRelayQuery,
      useMaintenanceLogsRelayPaginationFragment$key
    >(MaintenanceLogsListPaginationFragment, queryData);

  const nodes =
    data.listMaintenanceLogs?.edges
      ?.map((edge) => edge?.node)
      .filter(Boolean) ?? [];

  return {
    data: nodes as MaintenanceLogGraphQL[],
    count: data.listMaintenanceLogs?.count,
    pageInfo: data.listMaintenanceLogs?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single maintenance log by ID (Suspense)
 */
export function useMaintenanceLogByIdRelay(logId: string) {
  const data = useLazyLoadQuery<useMaintenanceLogsRelayByIdQuery>(
    MaintenanceLogByIdQuery,
    { id: logId }
  );

  return data.getMaintenanceLog as MaintenanceLogGraphQL | null;
}

/**
 * Non-Suspense hook for fetching a single maintenance log by ID.
 * Provides React Query-like interface with isLoading, data, refetch.
 */
export function useMaintenanceLogByIdRelayQuery(
  logId: string,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<MaintenanceLogGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && !!logId;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (!isFetched) {
          setIsLoading(true);
        }

        const result =
          await fetchQuery<useMaintenanceLogsRelayByIdQuery>(
            relayEnvironment,
            MaintenanceLogByIdQuery,
            { id: logId }
          ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(
            (result.getMaintenanceLog as MaintenanceLogGraphQL) ?? null
          );
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch maintenance log')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, logId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    isLoading,
    isFetched,
    error,
    refetch,
  };
}

/**
 * Hook that provides React Query-like interface for maintenance logs data.
 * Does NOT use Suspense.
 */
export function useMaintenanceLogsRelayQuery(
  filter?: MaintenanceLogQueryFilter,
  options?: {
    limit?: number;
    sort?: MaintenanceLogSortInput[];
    enabled?: boolean;
    after?: string | null;
  }
) {
  const [data, setData] = useState<MaintenanceLogGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () =>
      options?.sort ?? [
        { field: 'CREATED_ON' as const, order: 'DESC' as const },
      ],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  const after = options?.after || undefined;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildLogRelayFilter(filter);
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (builtFilter) {
          variables.filter = builtFilter;
        }

        const result =
          await fetchQuery<UseMaintenanceLogsRelayDirectQuery>(
            relayEnvironment,
            MaintenanceLogsListDirectQuery,
            variables as useMaintenanceLogsRelayQuery$variables
          ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listMaintenanceLogs?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as MaintenanceLogGraphQL[]);
          setCount(result.listMaintenanceLogs?.count ?? undefined);
          setHasMore(
            result.listMaintenanceLogs?.pageInfo?.hasNextPage ?? false
          );
          setEndCursor(
            result.listMaintenanceLogs?.pageInfo?.endCursor ?? undefined
          );
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch maintenance logs')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function buildLogRelayFilter(
  filter?: MaintenanceLogQueryFilter
): MaintenanceLogFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: MaintenanceLogFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.locationId) {
    relayFilter.locationId = { eq: filter.locationId };
  }

  // Search workaround: use `or` filter with notes ilike
  if (filter.search) {
    const searchPattern = `%${filter.search}%`;
    relayFilter.or = [
      { notes: { ilike: searchPattern } },
      { maintenanceTask: { name: { ilike: searchPattern } } },
    ];
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildLogRelayFilter };
